import React, { useState, useEffect } from 'react';
import PageContentService from '../services/pageContentService';
import { useInlineEdit } from '../components/admin/InlineEditContext';
import InlineEditableText from '../components/admin/InlineEditableText';

const About = () => {
  const [page, setPage] = useState(null);
  const inlineCtx = useInlineEdit && useInlineEdit();

  useEffect(() => {
    (async () => {
      try {
        const p = await PageContentService.getPage('apropos');
        setPage(p);
      } catch (_e) {
        setPage(null);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto space-y-12">
          <div className="text-center">
            {(() => {
              const title = page?.title?.fr || page?.title || 'À Propos de 2RENOV';
              const sections = Array.isArray(page?.sections) ? page.sections : [];
              const heroSec = sections.find(s => (s.key === 'hero' || s.type === 'hero')) || null;
              const subtitle = heroSec?.data?.subtitle?.fr || heroSec?.data?.sous_titre || '';
              return (
                <>
                  <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                    {inlineCtx ? (
                      <InlineEditableText
                        value={inlineCtx.getValue('hero', 'title.fr', title)}
                        sectionKey="hero"
                        fieldPath="title.fr"
                        placeholder="À Propos de 2RENOV"
                      />
                    ) : title}
                  </h1>
                  <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto">
                    {inlineCtx ? (
                      <InlineEditableText
                        value={inlineCtx.getValue('hero', 'subtitle.fr', subtitle)}
                        sectionKey="hero"
                        fieldPath="subtitle.fr"
                        placeholder="Votre partenaire de confiance pour tous vos projets de rénovation"
                      />
                    ) : (subtitle || 'Votre partenaire de confiance pour tous vos projets de rénovation')}
                  </p>
                </>
              );
            })()}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-800 rounded-lg p-8">
              {(() => {
                const sections = Array.isArray(page?.sections) ? page.sections : [];
                const sec = sections.find(s => (s.key === 'mission' || s.type === 'mission')) || null;
                const t = sec?.data?.title?.fr || sec?.title || 'Notre Mission';
                const d = sec?.data?.description?.fr || '';
                return (
                  <>
                    <h2 className="text-2xl md:text-3xl font-semibold mb-6 text-white">
                      {inlineCtx ? (
                        <InlineEditableText
                          value={inlineCtx.getValue('mission', 'title.fr', t)}
                          sectionKey="mission"
                          fieldPath="title.fr"
                          placeholder="Notre Mission"
                        />
                      ) : t}
                    </h2>
                    <p className="text-gray-300 text-lg leading-relaxed">
                      {inlineCtx ? (
                        <InlineEditableText
                          value={inlineCtx.getValue('mission', 'description.fr', d)}
                          sectionKey="mission"
                          fieldPath="description.fr"
                          placeholder={"Chez 2RENOV, nous nous engageons à transformer vos espaces en lieux d'exception. Notre mission est de créer des environnements qui allient esthétique, fonctionnalité et durabilité, tout en respectant vos besoins et votre budget."}
                          multiline
                        />
                      ) : (d || "Chez 2RENOV, nous nous engageons à transformer vos espaces en lieux d'exception. Notre mission est de créer des environnements qui allient esthétique, fonctionnalité et durabilité, tout en respectant vos besoins et votre budget.")}
                    </p>
                  </>
                );
              })()}
            </div>

            <div className="bg-gray-800 rounded-lg p-8">
              {(() => {
                const sections = Array.isArray(page?.sections) ? page.sections : [];
                const sec = sections.find(s => (s.key === 'expertise' || s.type === 'expertise')) || null;
                const t = sec?.data?.title?.fr || sec?.title || 'Notre Expertise';
                const d = sec?.data?.description?.fr || '';
                return (
                  <>
                    <h2 className="text-2xl md:text-3xl font-semibold mb-6 text-white">
                      {inlineCtx ? (
                        <InlineEditableText
                          value={inlineCtx.getValue('expertise', 'title.fr', t)}
                          sectionKey="expertise"
                          fieldPath="title.fr"
                          placeholder="Notre Expertise"
                        />
                      ) : t}
                    </h2>
                    <p className="text-gray-300 text-lg leading-relaxed">
                      {inlineCtx ? (
                        <InlineEditableText
                          value={inlineCtx.getValue('expertise', 'description.fr', d)}
                          sectionKey="expertise"
                          fieldPath="description.fr"
                          placeholder={"Avec plus de 10 ans d'expérience dans le domaine de la rénovation, notre équipe d'experts qualifiés maîtrise tous les aspects de la construction et de la rénovation, du design à la réalisation."}
                          multiline
                        />
                      ) : (d || "Avec plus de 10 ans d'expérience dans le domaine de la rénovation, notre équipe d'experts qualifiés maîtrise tous les aspects de la construction et de la rénovation, du design à la réalisation.")}
                    </p>
                  </>
                );
              })()}
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-8">
            {(() => {
              const sections = Array.isArray(page?.sections) ? page.sections : [];
              const sec = sections.find(s => (s.key === 'values' || s.type === 'values')) || null;
              const t = sec?.data?.title?.fr || sec?.title || 'Nos Valeurs';
              const items = Array.isArray(sec?.data?.items) ? sec.data.items : [];
              const fallback = [
                { title: 'Qualité', description: "Nous nous engageons à fournir un travail de la plus haute qualité, en utilisant les meilleurs matériaux et techniques." },
                { title: 'Innovation', description: 'Nous restons à la pointe des dernières tendances et technologies pour offrir des solutions innovantes.' },
                { title: 'Satisfaction', description: 'La satisfaction de nos clients est notre priorité absolue. Nous travaillons en étroite collaboration avec vous pour réaliser vos projets selon vos attentes.' }
              ];
              const renderItems = items.length ? items : fallback;
              return (
                <>
                  <h2 className="text-2xl md:text-3xl font-semibold mb-8 text-white text-center">
                    {inlineCtx ? (
                      <InlineEditableText
                        value={inlineCtx.getValue('values', 'title.fr', t)}
                        sectionKey="values"
                        fieldPath="title.fr"
                        placeholder="Nos Valeurs"
                      />
                    ) : t}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {renderItems.map((it, idx) => (
                      <div key={idx} className="bg-gray-700 rounded-lg p-6 text-center">
                        <div className="bg-primary w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <h3 className="text-xl font-semibold mb-4 text-white">
                          {inlineCtx ? (
                            <InlineEditableText
                              value={(it.title || it.titre || '')}
                              sectionKey="values"
                              fieldPath={`items[${idx}].title`}
                              placeholder={it.title || it.titre || 'Titre'}
                            />
                          ) : (it.title || it.titre)}
                        </h3>
                        <p className="text-gray-300 leading-relaxed">
                          {inlineCtx ? (
                            <InlineEditableText
                              value={(it.description || it.texte || '')}
                              sectionKey="values"
                              fieldPath={`items[${idx}].description`}
                              placeholder={it.description || it.texte || 'Description'}
                              multiline
                            />
                          ) : (it.description || it.texte)}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Section Histoire de l'entreprise */}
          <div className="bg-gray-800 rounded-lg p-8">
            {(() => {
              const sections = Array.isArray(page?.sections) ? page.sections : [];
              const sec = sections.find(s => (s.key === 'histoire' || s.type === 'histoire' || s.key === 'history' || s.type === 'history')) || null;
              const t = sec?.data?.title?.fr || sec?.title || 'Notre Histoire';
              const p1 = (sec?.data?.paragraphs?.fr && Array.isArray(sec.data.paragraphs.fr) ? sec.data.paragraphs.fr[0] : '') || sec?.data?.description?.fr || '';
              const p2 = (sec?.data?.paragraphs?.fr && Array.isArray(sec.data.paragraphs.fr) ? sec.data.paragraphs.fr[1] : '') || '';
              const p3 = (sec?.data?.paragraphs?.fr && Array.isArray(sec.data.paragraphs.fr) ? sec.data.paragraphs.fr[2] : '') || '';
              const statsSec = sections.find(s => (s.key === 'stats' || s.type === 'stats')) || null;
              const stats = Array.isArray(statsSec?.data?.items) ? statsSec.data.items : [];
              const fallbackStats = [
                { number: '500+', label_fr: 'Projets réalisés' },
                { number: '10+', label_fr: "Années d'expérience" },
                { number: '15', label_fr: 'Équipe' },
                { number: '98%', label_fr: 'Satisfaction client' }
              ];
              const renderStats = stats.length ? stats : fallbackStats;
              return (
                <>
                  <h2 className="text-2xl md:text-3xl font-semibold mb-8 text-white text-center">
                    {inlineCtx ? (
                      <InlineEditableText
                        value={inlineCtx.getValue('histoire', 'title.fr', t)}
                        sectionKey="histoire"
                        fieldPath="title.fr"
                        placeholder="Notre Histoire"
                      />
                    ) : t}
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                    <div>
                      <p className="text-gray-300 text-lg leading-relaxed mb-6">
                        {inlineCtx ? (
                          <InlineEditableText
                            value={p1}
                            sectionKey="histoire"
                            fieldPath="paragraphs.fr[0]"
                            placeholder={"Fondée en 2014, 2RENOV a débuté comme une petite entreprise familiale avec une vision claire : révolutionner le secteur de la rénovation en plaçant la satisfaction client au cœur de nos préoccupations."}
                            multiline
                          />
                        ) : (p1 || "Fondée en 2014, 2RENOV a débuté comme une petite entreprise familiale avec une vision claire : révolutionner le secteur de la rénovation en plaçant la satisfaction client au cœur de nos préoccupations.")}
                      </p>
                      {p2 ? (
                        <p className="text-gray-300 text-lg leading-relaxed mb-6">
                          {inlineCtx ? (
                            <InlineEditableText
                              value={p2}
                              sectionKey="histoire"
                              fieldPath="paragraphs.fr[1]"
                              placeholder={p2}
                              multiline
                            />
                          ) : p2}
                        </p>
                      ) : null}
                      {p3 ? (
                        <p className="text-gray-300 text-lg leading-relaxed">
                          {inlineCtx ? (
                            <InlineEditableText
                              value={p3}
                              sectionKey="histoire"
                              fieldPath="paragraphs.fr[2]"
                              placeholder={p3}
                              multiline
                            />
                          ) : p3}
                        </p>
                      ) : null}
                    </div>
                    <div className="bg-gray-700 rounded-lg p-6">
                      <h3 className="text-xl font-semibold mb-4 text-white">Quelques chiffres</h3>
                      <div className="space-y-4">
                        {renderStats.map((it, idx) => (
                          <div key={idx} className="flex justify-between items-center">
                            <span className="text-gray-300">{it.label_fr || it.label}</span>
                            <span className="text-primary font-bold text-xl">{it.number || it.value || it.nombre_elements}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

        
      )}
    </div>
  );
};

export default About;
